import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '../constants/theme';
import { CUOTA_TARJETA_ENABLED, SUSCRIPCION_STRIPE_ENABLED } from '../constants/featureFlags';
import { useAppStore } from '../store/useAppStore';
import { Platform } from 'react-native';
import { db } from '../services/firebaseConfig';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
// SDK NATIVO de Firestore. En Android el SDK web va SIN SESIÓN (el login vive
// en @react-native-firebase vía authBridge) y la regla de /users/{uid} exige
// isSelf(uid): la lectura se denegaba y `driverDoc` quedaba en null para
// SIEMPRE. Con null esta pantalla afirmaba "Prueba aún no iniciada", ocultaba
// los días restantes y NUNCA detectaba driverBlockedNoPayment — o sea, un
// chofer bloqueado por impago veía una pantalla tranquilizadora.
// Mismo patrón que services/firestoreService.ts.
import nativeFirestore from '@react-native-firebase/firestore';
import { StripeService } from '../services/stripe';
import { firestoreService } from '../services/firestoreService';
import * as WebBrowser from 'expo-web-browser';
import { pesos } from '../utils/dinero';

interface DriverDoc {
  // El rol y el vehículo TAL COMO ESTÁN EN EL SERVIDOR. Son los que usa
  // `getMonthlyFee` de functions/src/loyalty.ts para facturar de verdad; el rol
  // del almacén local puede ir por detrás (el chofer cambió de vehículo, un
  // admin le cambió el rol, o la app arrancó con el valor cacheado).
  role?: string | null;
  vehicleType?: string;
  // Datos que identifican el vehículo en la calle. Los escribe el chofer
  // (kyc-documents.tsx) y son los que el pasajero necesita para saber qué
  // carro esperar y comprobar la placa antes de montarse. Se leen aquí SOLO
  // para avisarle si le faltan — esta pantalla no los escribe.
  vehicleBrand?: string | null;
  vehicleModel?: string | null;
  vehicleColor?: string | null;
  licensePlate?: string | null;
  driverStatus?: 'TRIAL' | 'ACTIVE' | null;
  driverTrialStartedAt?: { toMillis: () => number };
  driverTrialEndsAt?: { toMillis: () => number };
  driverTrialReminderSent?: boolean;
  driverBlockedNoPayment?: boolean;
  subscriptionCredit?: number;       // CRÉDITO hacia próxima cuota mensual
  customMonthlyFee?: number;          // opcional, sobreescribe la cuota por rol
  stripePaymentMethodId?: string;
  walletBalance?: number;
  // Stripe Connect (recibir payouts).
  stripeAccountId?: string;
  stripeChargesEnabled?: boolean;
  stripePayoutsEnabled?: boolean;
  stripeOnboardingCompleted?: boolean;
  // Dónde recibir incentivos: 'WALLET' (saldo/recarga) o 'STRIPE' (retiro banco).
  // 'STRIPE' queda solo por compatibilidad con perfiles viejos: Stripe no puede
  // pagarle a un chofer dominicano, así que ese valor se trata como 'WALLET'.
  incentivePayoutMethod?: 'WALLET' | 'TRANSFER' | 'STRIPE';
}

// Cuota mensual oficial (espejo del server). Se calcula por vehículo.
function getMonthlyFeeMirror(role?: string | null, vehicleType?: string): number {
  if (role === 'SCHOOL_DRIVER') return 2500;
  switch (vehicleType) {
    case 'MOTORCYCLE':
    case 'BICYCLE':
      return 1000;
    case 'CAR':
      return 1300;
    case 'MINIBUS':
      return 1500;
    default:
      if (role === 'DELIVERY_DRIVER') return 1000;
      return 1300;
  }
}

function daysBetween(future: number, now: number): number {
  return Math.max(0, Math.ceil((future - now) / (24 * 60 * 60 * 1000)));
}

/** Roles que conducen y, por tanto, tienen un vehículo que registrar. */
function esRolDeChofer(rol?: string | null): boolean {
  return rol === 'DRIVER' || rol === 'SCHOOL_DRIVER' || rol === 'DELIVERY_DRIVER';
}

/**
 * Qué datos del vehículo NO están guardados en el documento del chofer, con el
 * nombre con el que se le habla (no el nombre técnico del campo).
 *
 * POR QUÉ EXISTE: se comprobó contra la base de datos real que hay choferes
 * VERIFICADOS y trabajando en línea sin `vehicleBrand`, `vehicleModel`,
 * `vehicleColor` ni `licensePlate` — los campos no existen en su documento. El
 * único sitio que los escribe es un paso del alta (kyc-documents.tsx) por el
 * que ya pasaron, así que el dato no se escribió nunca y la app jamás se lo
 * dijo: se enteraban porque el pasajero se quejaba de que veía un carrito
 * genérico. El AÑO no cuenta: es opcional en el formulario.
 */
function faltantesDeVehiculo(d: DriverDoc | null): string[] {
  const faltan: string[] = [];
  if (!String(d?.vehicleBrand ?? '').trim()) faltan.push('la marca');
  if (!String(d?.vehicleModel ?? '').trim()) faltan.push('el modelo');
  if (!String(d?.vehicleColor ?? '').trim()) faltan.push('el color');
  if (!String(d?.licensePlate ?? '').trim()) faltan.push('la placa');
  return faltan;
}

export default function DriverSubscriptionScreen() {
  const router = useRouter();
  const userFirebaseUid = useAppStore((s) => s.userFirebaseUid);
  const userRole = useAppStore((s) => s.userRole);

  const [driverDoc, setDriverDoc] = useState<DriverDoc | null>(null);
  // `driverDoc === null` significaba dos cosas incompatibles: "todavía no
  // cargó / no existe" y "no pudimos leerlo". Separarlas es lo que permite no
  // mentirle al chofer sobre su prueba ni sobre un bloqueo por impago.
  const [perfilRecibido, setPerfilRecibido] = useState(false);
  const [errorPerfil, setErrorPerfil] = useState<string | null>(null);
  // Sin sesión no hay nada que esperar: si no, la pantalla se quedaría
  // "Consultando…" para siempre porque el efecto sale antes de suscribirse.
  const perfilListo = !userFirebaseUid || perfilRecibido;

  // ─── MEMBRESÍA POR TARJETA (extensión de Stripe en Firebase) ───────────
  // Los planes NO están en el código: los lee del catálogo que la extensión
  // `firestore-stripe-payments` sincroniza desde tu panel de Stripe. Creas
  // un Producto con su Precio recurrente allá y aparece aquí solo, sin
  // recompilar la app ni publicar versión nueva en Play Store.
  const [planes, setPlanes] = useState<
    Array<{ productId: string; priceId: string; nombre: string; descripcion: string; monto: number; moneda: string; intervalo: string }>
  >([]);
  const [cargandoPlanes, setCargandoPlanes] = useState(true);
  const [abriendoCheckout, setAbriendoCheckout] = useState<string | null>(null);

  useEffect(() => {
    // Apagada: la cuota se paga como pago único con tarjeta desde Billetera
    // (ver SUSCRIPCION_STRIPE_ENABLED). Sin esto, cualquier producto que se
    // sincronizara en la cuenta de Stripe —compartida con otras apps— saldría
    // aquí como un plan que el chofer puede contratar.
    if (!SUSCRIPCION_STRIPE_ENABLED) {
      setCargandoPlanes(false);
      return;
    }
    let vivo = true;
    StripeService.listSubscriptionPlans()
      .then((p) => { if (vivo) setPlanes(p); })
      .catch(() => { /* sin planes: la tarjeta no se muestra */ })
      .finally(() => { if (vivo) setCargandoPlanes(false); });
    return () => { vivo = false; };
  }, []);

  /**
   * Abre el Checkout de Stripe para suscribirse.
   *
   * Camino: la app escribe en `customers/{uid}/checkout_sessions` → la
   * extensión lo detecta, crea la sesión en Stripe y devuelve la URL en el
   * mismo documento → la abrimos en el navegador del sistema.
   *
   * Esto NO pasa por Railway: es Firebase hablando con Stripe directamente.
   * Por eso funciona aunque el backend viejo esté borrado.
   */
  const suscribirse = async (priceId: string, nombrePlan: string) => {
    if (!userFirebaseUid) {
      Alert.alert('Sesión requerida', 'Inicia sesión para contratar tu membresía.');
      return;
    }
    setAbriendoCheckout(priceId);
    try {
      const url = await StripeService.createSubscription(userFirebaseUid, priceId);
      if (!url) {
        Alert.alert(
          'No pudimos abrir el pago',
          'Stripe no respondió a tiempo. Verifica tu conexión e intenta de nuevo en un momento.'
        );
        return;
      }
      await WebBrowser.openBrowserAsync(url);
      // Al volver, el webhook de Stripe ya habrá actualizado la suscripción.
      // El listener de driverDoc refleja el cambio solo.
    } catch (e: any) {
      console.warn('[suscribirse] falló:', e?.message);
      Alert.alert('No pudimos abrir el pago', 'Intenta de nuevo en un momento.');
    } finally {
      setAbriendoCheckout(null);
    }
  };
  const [now, setNow] = useState(Date.now());
  const [savingPref, setSavingPref] = useState(false);

  // A dónde va el bono. Antes el valor por defecto era 'STRIPE' y la pantalla
  // prometía "se retiran a tu cuenta bancaria" — pero ninguna función del
  // servidor leía esta preferencia y Stripe no puede pagarle a un chofer
  // dominicano. Ahora: por defecto al SALDO (le sirve enseguida, descuenta su
  // cuota), y si elige TRANSFERENCIA se le deposita a su cuenta, que es lo que
  // el servidor hace de verdad (ver functions/src/incentives.ts → pagarBono).
  const incentiveMethod: 'WALLET' | 'TRANSFER' =
    driverDoc?.incentivePayoutMethod === 'TRANSFER' ? 'TRANSFER' : 'WALLET';
  const setIncentiveMethod = async (method: 'WALLET' | 'TRANSFER') => {
    if (!userFirebaseUid || method === incentiveMethod || savingPref) return;
    setSavingPref(true);
    try {
      await firestoreService.updateUserProfile(userFirebaseUid, { incentivePayoutMethod: method });
    } catch {
      Alert.alert('Error', 'No se pudo guardar tu preferencia. Intenta de nuevo.');
    } finally {
      setSavingPref(false);
    }
  };

  // Listener Firestore al perfil del driver — actualiza días restantes en vivo
  useEffect(() => {
    if (!userFirebaseUid) return;

    const recibir = (data: DriverDoc | null) => {
      setDriverDoc(data);
      setErrorPerfil(null);
      setPerfilRecibido(true);
    };
    const fallar = (msg: string) => {
      console.warn('No se pudo leer perfil de chofer:', msg);
      setErrorPerfil(msg);
      setPerfilRecibido(true);
    };

    if (Platform.OS !== 'web') {
      const unsubNat = nativeFirestore()
        .collection('users')
        .doc(userFirebaseUid)
        .onSnapshot(
          // En @react-native-firebase v24 `exists` es MÉTODO, no propiedad.
          (snap: any) => recibir(snap?.exists?.() ? (snap.data() as DriverDoc) : null),
          (e: any) => fallar(String(e?.message ?? e))
        );
      return () => unsubNat();
    }

    const ref = doc(db, 'users', userFirebaseUid);
    const unsub = onSnapshot(
      ref,
      (snap) => recibir(snap.exists() ? (snap.data() as DriverDoc) : null),
      (err) => fallar(String(err?.message ?? err))
    );
    return () => unsub();
  }, [userFirebaseUid]);

  // V25 — el vehículo VERIFICADO del KYC, el mismo que usa el servidor para
  // cobrar (`vehiculoDeLaCuota`). Solo lo escribe el servidor; el dueño puede
  // leerlo. Si no existe o falla, null: se usa el suelto, igual que el servidor.
  const [vehiculoVerificado, setVehiculoVerificado] = useState<string | null>(null);
  useEffect(() => {
    if (!userFirebaseUid) { setVehiculoVerificado(null); return; }
    let vivo = true;
    const tomar = (data: any) => {
      const v = String(data?.vehicleTypeVerificado ?? '').trim().toUpperCase();
      if (vivo) setVehiculoVerificado(v || null);
    };
    (async () => {
      try {
        if (Platform.OS !== 'web') {
          const snap: any = await nativeFirestore().collection('kyc_submissions').doc(userFirebaseUid).get();
          tomar(snap?.exists?.() ? snap.data() : null);
        } else {
          const snap = await getDoc(doc(db, 'kyc_submissions', userFirebaseUid));
          tomar(snap.exists() ? snap.data() : null);
        }
      } catch (e: any) {
        console.warn('No se pudo leer el vehículo verificado:', e?.message ?? e);
        if (vivo) setVehiculoVerificado(null);
      }
    })();
    return () => { vivo = false; };
  }, [userFirebaseUid]);

  // Recalcular días cada 60s mientras la pantalla esté abierta
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const trialEndsMs = driverDoc?.driverTrialEndsAt?.toMillis?.();
  const daysLeft = useMemo(() => (trialEndsMs ? daysBetween(trialEndsMs, now) : null), [trialEndsMs, now]);
  const isBlocked = !!driverDoc?.driverBlockedNoPayment;
  const credit = Number(driverDoc?.subscriptionCredit ?? 0);
  const driverVehicleType =
    vehiculoVerificado || String(driverDoc?.vehicleType ?? '').trim().toUpperCase() || undefined;
  // LA CUOTA SE CALCULA CON EL ROL DEL DOCUMENTO, no con el del almacén local.
  // El servidor factura leyendo `users/{uid}.role` (functions/src/loyalty.ts →
  // getMonthlyFee). Si aquí usábamos `userRole` del store —que puede estar
  // desfasado— la pantalla prometía una cuota y el cobro era otro: p. ej. un
  // chofer escolar (DOP 2,500) veía DOP 1,300 mientras el store aún decía
  // DRIVER. Solo caemos al store si el documento todavía no trae rol.
  const rolFacturable = driverDoc?.role ?? userRole;
  const monthlyFee = Number(
    driverDoc?.customMonthlyFee ?? getMonthlyFeeMirror(rolFacturable, driverVehicleType)
  );
  const creditCoveragePct = Math.min(100, Math.round((credit / monthlyFee) * 100));
  const remainingFee = Math.max(0, monthlyFee - credit);

  // ─── ¿TIENE EL VEHÍCULO REGISTRADO? ────────────────────────────────────
  // Se afirma SOLO con el documento del servidor en la mano: si la lectura
  // falló o todavía no llegó, no se dice nada. Un aviso falso mandaría al
  // chofer a "arreglar" algo que ya está bien, y esta pantalla ya tuvo ese
  // problema con el estado de la prueba.
  const faltantesVehiculo = faltantesDeVehiculo(driverDoc);
  const avisarVehiculoIncompleto =
    perfilListo &&
    !errorPerfil &&
    !!driverDoc &&
    esRolDeChofer(rolFacturable) &&
    faltantesVehiculo.length > 0;
  // "y" antes del último, para que se lea como una frase y no como una lista
  // de campos de base de datos.
  const textoFaltantes = faltantesVehiculo.join(', ').replace(/, ([^,]*)$/, ' y $1');
  const esMoto = driverVehicleType === 'MOTORCYCLE';

  /**
   * Lo lleva al ÚNICO sitio que escribe esos campos (el formulario de
   * kyc-documents), ya desplazado hasta él y con su tipo de vehículo, para
   * que los ejemplos de placa y los textos hablen de su motor o de su carro.
   * `focoVehiculo=1` es lo que allí destapa el botón de guardar sin repetir
   * la verificación.
   */
  const irACompletarVehiculo = () => {
    router.push({
      pathname: '/kyc-documents',
      params: { vehicle: driverDoc?.vehicleType ?? '', focoVehiculo: '1' },
    } as any);
  };

  // CÓDIGO MUERTO ELIMINADO: `handleConnectOnboarding` y su `connectLoading`.
  // Llamaba a `StripeService.createConnectOnboarding`, que apunta al backend de
  // Railway — BORRADO, responde 404 en todas sus rutas. Ya no lo invocaba ningún
  // botón (la tarjeta de Stripe Connect se quitó de esta pantalla porque Stripe
  // no puede pagarle a un chofer dominicano), así que era una vía a
  // infraestructura apagada esperando a que alguien la volviera a cablear.
  // El chofer cobra directo del pasajero; su cuenta de banco se registra en
  // /driver-bank-accounts, que sí funciona.

  // CÓDIGO MUERTO ELIMINADO (19-sep-2026): `handleAddCard` guardaba una
  // tarjeta pidiéndole un SetupIntent a Railway (borrado). Solo lo llamaba la
  // tarjeta de "método de pago" que estaba oculta tras PLATFORM_CARD_ENABLED.
  // La cuota con tarjeta es ahora un pago único desde Billetera
  // (functions/src/pagoCuotaStripe.ts): no hace falta guardar ninguna tarjeta.

  // Banner del estado del trial
  const renderStatusBanner = () => {
    // Sin sesión no hay membresía que consultar; decirlo es más útil que un
    // "Prueba aún no iniciada" que suena a que ya tiene cuenta de chofer.
    if (!userFirebaseUid) {
      return (
        <View style={[styles.banner, styles.bannerNeutral]}>
          <Ionicons name="log-in" size={28} color="#0277BD" />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={[styles.bannerTitle, { color: '#0277BD' }]}>Inicia sesión</Text>
            <Text style={styles.bannerSub}>
              Necesitamos tu cuenta para mostrarte el estado de tu prueba y tu cuota.
            </Text>
          </View>
        </View>
      );
    }

    // Mientras no sepamos nada, no afirmamos nada.
    if (!perfilListo) {
      return (
        <View style={[styles.banner, styles.bannerNeutral]}>
          <ActivityIndicator color="#0277BD" />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={[styles.bannerTitle, { color: '#0277BD' }]}>Consultando tu membresía…</Text>
          </View>
        </View>
      );
    }

    // Si la lectura falló, decirlo. Antes caía en "Prueba aún no iniciada",
    // que es exactamente lo contrario de lo que le conviene saber a un chofer
    // que podría estar BLOQUEADO por falta de pago.
    if (errorPerfil) {
      return (
        <View style={[styles.banner, styles.bannerError]}>
          <Ionicons name="cloud-offline" size={28} color="#C62828" />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={[styles.bannerTitle, { color: '#C62828' }]}>
              No pudimos leer el estado de tu membresía
            </Text>
            <Text style={styles.bannerSub}>
              No sabemos si tu prueba sigue activa ni si tu cuenta está al día, así que
              no te mostramos ningún dato para no equivocarnos. Revisa tu conexión y
              vuelve a entrar a esta pantalla.
            </Text>
          </View>
        </View>
      );
    }

    if (!driverDoc?.driverStatus) {
      return (
        <View style={[styles.banner, styles.bannerNeutral]}>
          <Ionicons name="information-circle" size={28} color="#0277BD" />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={[styles.bannerTitle, { color: '#0277BD' }]}>Prueba aún no iniciada</Text>
            <Text style={styles.bannerSub}>
              Al completar tu primer viaje o entrega, arrancan automáticamente 30 días sin cuota
              mensual. Mientras tanto puedes ir dejando saldo en tu billetera para no preocuparte
              cuando termine la prueba.
            </Text>
          </View>
        </View>
      );
    }

    if (driverDoc.driverStatus === 'TRIAL') {
      const isCritical = (daysLeft ?? 99) <= 2;
      return (
        <View style={[styles.banner, isCritical ? styles.bannerWarn : styles.bannerSuccess]}>
          <Ionicons
            name={isCritical ? 'time' : 'gift'}
            size={28}
            color={isCritical ? '#E65100' : '#1B5E20'}
          />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={[styles.bannerTitle, { color: isCritical ? '#E65100' : '#1B5E20' }]}>
              {daysLeft === 0
                ? 'Tu prueba termina hoy'
                : `${daysLeft} ${daysLeft === 1 ? 'día' : 'días'} restantes de prueba`}
            </Text>
            <Text style={styles.bannerSub}>
              {isCritical
                // NO se le puede pedir que agregue una tarjeta: toda la
                // tarjeta de método de pago con tarjeta está detrás de
                // PLATFORM_CARD_ENABLED (línea 612), que hoy es `false`. Le
                // pedíamos algo que en esta misma pantalla no existe. Lo que
                // sí puede hacer, y está abajo con su botón, es recargar.
                ? 'Sube ya tu comprobante de recarga —depósito o transferencia—: cuenta cuando lo aprobamos, y tiene que estar aprobado antes de que termine la prueba para seguir aceptando viajes sin pausa.'
                : '¡Estás manejando GRATIS! Aprovecha estos días para ganar clientes habituales.'}
            </Text>
          </View>
        </View>
      );
    }

    // ACTIVE
    if (isBlocked) {
      return (
        <View style={[styles.banner, styles.bannerError]}>
          <Ionicons name="lock-closed" size={28} color="#C62828" />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={[styles.bannerTitle, { color: '#C62828' }]}>
              🚫 Cuenta bloqueada por falta de pago
            </Text>
            {/* Mismo caso que el banner de prueba crítica: aquí no hay forma
                de agregar una tarjeta (PLATFORM_CARD_ENABLED en `false`). */}
            <Text style={styles.bannerSub}>
              {/* V05 — si lo que tiene a favor ya cubre la cuota, el repaso
                  diario del servidor (expireDriverTrials) le levanta el bloqueo. */}
              {credit + Math.max(0, Number(driverDoc?.walletBalance ?? 0)) >=
                (Number((driverDoc as any)?.pendingCharge?.amount) > 0 ? Number((driverDoc as any).pendingCharge.amount) : monthlyFee)
                ? 'Lo que tienes a favor ya cubre tu membresía. Tu cuenta se reactiva sola en menos de 24 horas.'
                : Number((driverDoc as any)?.pendingCharge?.amount) > 0
                  // Pausa por la membresía del mes (no por la prueba): se dice cuánto falta.
                  ? `Tu cuenta está pausada porque la membresía no está pagada: faltan ${pesos(Math.max(0, Number((driverDoc as any).pendingCharge.amount) - credit - Math.max(0, Number(driverDoc?.walletBalance ?? 0))))}. ${CUOTA_TARJETA_ENABLED ? 'Págala con tarjeta desde Billetera y se reactiva en cuanto Stripe confirme el pago, o recarga' : 'Recarga'} tu billetera con tu comprobante y se reactiva en cuanto lo aprobemos.`
                  : CUOTA_TARJETA_ENABLED
                    ? 'Tu prueba terminó sin pago registrado. Paga tu cuota con tarjeta desde Billetera y vuelves en cuanto Stripe confirme el pago, o recarga con tu comprobante de depósito o transferencia.'
                    : 'Tu prueba terminó sin pago registrado. No podrás aceptar viajes hasta que recargues tu billetera con tu comprobante de depósito o transferencia.'}
            </Text>
          </View>
        </View>
      );
    }
    return (
      <View style={[styles.banner, styles.bannerSuccess]}>
        <Ionicons name="checkmark-circle" size={28} color="#1B5E20" />
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Text style={[styles.bannerTitle, { color: '#1B5E20' }]}>Cuenta activa</Text>
          {/* ── "SE COBRARÁ AUTOMÁTICAMENTE" NO ES VERDAD ─────────────────
              Lo que el día 1 de cada mes hace `applyMonthlyCharge`
              (functions/src/loyalty.ts) es APLICAR lo que el chofer ya puso:
              primero su crédito de viajes gratis y después el saldo de su
              billetera. Lo que quede sin cubrir se guarda como `pendingCharge`
              — una deuda, no un cobro. El único cobro de verdad "solo" sería
              el de tarjeta vía Railway, y (a) PLATFORM_CARD_ENABLED está en
              `false`, así que no hay tarjeta que cobrar, y (b) el propio
              cliente de Railway advierte que "no está comprobado que Railway
              siga vivo" (functions/src/railwayClient.ts). Los Términos (punto
              6) lo dicen igual: no hay débito automático ni cargo a ninguna
              tarjeta; la cuota se paga por transferencia o depósito. */}
          <Text style={styles.bannerSub}>
            Estás operando como chofer Go Safely. El día 1 de cada mes aplicamos tu crédito y tu
            saldo a la cuota; lo que quede sin cubrir lo pagas {CUOTA_TARJETA_ENABLED ? 'con tarjeta o ' : ''}recargando
            la billetera por transferencia o depósito.
          </Text>
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => (router.canGoBack() ? router.back() : router.replace('/'))}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color={Colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Mi cuota de servicio</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView contentContainerStyle={styles.container}>
        {renderStatusBanner()}

        {/* ── VEHÍCULO SIN REGISTRAR ─────────────────────────────────────
            Va ARRIBA, junto al estado de la membresía, porque es de la misma
            familia: cosas que le impiden trabajar bien y que hasta ahora no
            tenía cómo enterarse. Dice exactamente qué falta y lleva al sitio
            donde se arregla. */}
        {avisarVehiculoIncompleto && (
          <TouchableOpacity
            style={styles.avisoVehiculo}
            onPress={irACompletarVehiculo}
            activeOpacity={0.85}
            accessibilityRole="button"
          >
            <Ionicons name="car-sport" size={26} color="#B26A00" />
            <View style={{ flex: 1, marginLeft: 12 }}>
              {/* El título dice la verdad exacta: "no está registrado" solo
                  cuando no hay NINGUNO de los cuatro datos. Si falta uno, es
                  otra cosa y decirlo mal le hace pensar que perdió todo. */}
              <Text style={styles.avisoVehiculoTitulo}>
                {faltantesVehiculo.length === 4
                  ? `Tu ${esMoto ? 'motor' : 'vehículo'} no está registrado`
                  : `Faltan datos de tu ${esMoto ? 'motor' : 'vehículo'}`}
              </Text>
              <Text style={styles.avisoVehiculoTexto}>
                Falta {textoFaltantes}. El pasajero que te pide un viaje no ve qué{' '}
                {esMoto ? 'motor' : 'carro'} esperar ni puede comprobar la placa al montarse.
              </Text>
              <Text style={styles.avisoVehiculoAccion}>Completar ahora →</Text>
            </View>
          </TouchableOpacity>
        )}

        {/* CRÉDITO HACIA PRÓXIMA CUOTA (NO es dinero líquido — es cobertura de cuota)
            Se oculta si no pudimos leer el perfil: un "DOP 0" inventado le hace
            creer al chofer que perdió el crédito que sí tiene acumulado. */}
        {!errorPerfil && perfilListo && (
        <View style={styles.subsidyCard}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            <Ionicons name="ribbon" size={20} color={Colors.primary} />
            <Text style={styles.subsidyTitle}>Crédito hacia tu próxima cuota</Text>
          </View>
          <Text style={styles.subsidyAmount}>{pesos(credit)}</Text>
          <Text style={styles.subsidySub}>
            Tu cuota mensual es {pesos(monthlyFee)}.
            {credit >= monthlyFee
              ? ' ¡Tu próxima cuota está totalmente cubierta! El sobrante queda para el siguiente mes.'
              : ` Te falta ${pesos(remainingFee)} (${100 - creditCoveragePct}%) para cubrirla por completo.`}
          </Text>
          {/* Barra de progreso */}
          <View style={styles.progressBg}>
            <View
              style={[
                styles.progressFill,
                { width: `${creditCoveragePct}%`, backgroundColor: creditCoveragePct >= 100 ? '#00C853' : Colors.primary },
              ]}
            />
          </View>
          <Text style={styles.progressLabel}>
            {creditCoveragePct >= 100
              ? '100% cubierto + saldo a favor'
              : `${creditCoveragePct}% cubierto por viajes gratis del pasajero`}
          </Text>
        </View>
        )}

        <View style={styles.howCreditWorks}>
          <Ionicons name="information-circle-outline" size={18} color={Colors.textSecondary} />
          {/* Los `**` de markdown NO los interpreta React Native: salían tal
              cual en la pantalla del chofer. La negrita se hace con un <Text>
              anidado. */}
          <Text style={styles.howCreditText}>
            Cada vez que cubres un viaje gratis a un pasajero (primer viaje o cada 10 viajes), el
            monto se acumula como crédito hacia tu cuota.{' '}
            <Text style={{ fontWeight: '800' }}>No es dinero líquido</Text> — es la manera
            en que Go Safely cubre tu cuota mensual cuando ayudas a fidelizar pasajeros.
          </Text>
        </View>

        {/* CÓMO COBRÁS TUS VIAJES — efectivo o transferencia directa a tu banco.
            (Se quitó el onboarding de Stripe Connect: NO opera para choferes en
            RD y desviaba los bonos al camino roto. El chofer cobra directo del
            pasajero — efectivo o transferencia — sin depender de Stripe.) */}
        <Text style={styles.sectionTitle}>Cómo cobrás tus viajes</Text>

        <View style={styles.methodCard}>
          <View style={[styles.methodIcon, { backgroundColor: '#00C853' }]}>
            <Ionicons name="cash" size={22} color="#FFF" />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.methodTitle}>Efectivo o transferencia — 100% para ti</Text>
            <Text style={styles.methodSub}>
              Cobras cada viaje directo del pasajero: en efectivo, o por transferencia a tu cuenta de banco. Go Safely no retiene nada de tu viaje.
            </Text>
          </View>
        </View>

        <View style={styles.methodCard}>
          <View style={[styles.methodIcon, { backgroundColor: Colors.primary }]}>
            <Ionicons name="business" size={22} color="#FFF" />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.methodTitle}>Tu cuenta de banco</Text>
            <Text style={styles.methodSub}>
              Regístrala para que los pasajeros te paguen por transferencia. Solo la ve un pasajero con un viaje activo contigo.
            </Text>
          </View>
          <TouchableOpacity style={styles.methodAction} onPress={() => router.push('/driver-bank-accounts' as any)}>
            <Text style={styles.methodActionText}>Registrar</Text>
          </TouchableOpacity>
        </View>

        <View style={[styles.helpBox, { marginTop: 6, marginBottom: 22 }]}>
          <Ionicons name="shield-checkmark" size={18} color={Colors.primary} />
          <Text style={styles.helpText}>
            Los bonos e incentivos se acumulan en tu billetera y te descuentan tu cuota mensual. El pago con tarjeta es para pagar TU cuota de servicio a Go Safely.
          </Text>
        </View>

        {/* MÉTODOS DE PAGO */}
        <Text style={styles.sectionTitle}>Cómo pagas a Go Safely</Text>

        {/* ── MEMBRESÍA POR TARJETA — extensión de Stripe ──────────────────
            Se muestra sola cuando hay planes activos en tu panel de Stripe.
            Si no has creado ninguno, no aparece nada (mejor que un botón que
            no lleva a ningún lado).

            Va detrás de SUSCRIPCION_STRIPE_ENABLED (apagada): la cuota se paga
            como pago único desde Billetera. Si algún día se enciende, ojo: el
            teléfono ya no puede crear checkout_sessions (firestore.rules), así
            que `suscribirse` tendría que pasar al servidor como crearPagoCuota. */}
        {SUSCRIPCION_STRIPE_ENABLED && planes.map((plan) => (
          <View key={plan.priceId} style={[styles.methodCard, styles.methodCardActive]}>
            <View style={[styles.methodIcon, { backgroundColor: '#635BFF' }]}>
              <Ionicons name="card" size={22} color="#FFF" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.methodTitle}>{plan.nombre}</Text>
              <Text style={styles.methodSub}>
                {plan.moneda === 'DOP' ? pesos(plan.monto) : `${plan.moneda} ${plan.monto.toLocaleString('es-DO')}`} / {plan.intervalo === 'month' ? 'mes' : plan.intervalo}
                {plan.descripcion ? ` · ${plan.descripcion}` : ''}
              </Text>
            </View>
            <TouchableOpacity
              style={styles.methodAction}
              onPress={() => suscribirse(plan.priceId, plan.nombre)}
              disabled={abriendoCheckout !== null}
            >
              {abriendoCheckout === plan.priceId ? (
                <ActivityIndicator color={Colors.primary} />
              ) : (
                <Text style={styles.methodActionText}>Pagar</Text>
              )}
            </TouchableOpacity>
          </View>
        ))}

        {!cargandoPlanes && planes.length === 0 && (
          CUOTA_TARJETA_ENABLED ? (
            <View style={styles.methodCard}>
              <View style={[styles.methodIcon, { backgroundColor: '#635BFF' }]}>
                <Ionicons name="card" size={22} color="#FFF" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.methodTitle}>Tarjeta de crédito o débito</Text>
                <Text style={styles.methodSub}>
                  Cuando tengas una cuota por pagar, págala con tarjeta desde Billetera: es un pago único en pesos y se acredita en cuanto Stripe lo confirma.
                </Text>
              </View>
              <TouchableOpacity style={styles.methodAction} onPress={() => router.push('/wallet')}>
                <Text style={styles.methodActionText}>Ir</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.methodCard}>
              <View style={[styles.methodIcon, { backgroundColor: '#9CA3AF' }]}>
                <Ionicons name="card-outline" size={22} color="#FFF" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.methodTitle}>Pago con tarjeta</Text>
                <Text style={styles.methodSub}>
                  Por ahora la cuota se paga recargando tu billetera por transferencia o depósito.
                </Text>
              </View>
            </View>
          )
        )}

        <View style={[styles.methodCard, (driverDoc?.walletBalance ?? 0) > 0 && styles.methodCardActive]}>
          <View style={[styles.methodIcon, { backgroundColor: '#00C853' }]}>
            <Ionicons name="wallet" size={22} color="#FFF" />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.methodTitle}>Recargar billetera</Text>
            <Text style={styles.methodSub}>
              {/* Un "DOP 0" cuando la lectura falló equivale a decirle que se
                  quedó sin saldo. Preferimos admitir que no lo sabemos. */}
              {errorPerfil || !perfilListo
                ? 'Saldo actual: no disponible ahora mismo'
                : `Saldo actual: ${pesos(driverDoc?.walletBalance ?? 0)}`}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.methodAction}
            onPress={() => router.push('/wallet')}
          >
            <Text style={styles.methodActionText}>Recargar</Text>
          </TouchableOpacity>
        </View>

        {/* El texto tiene que decir la verdad de lo que HAY HOY en pantalla.
            Con PLATFORM_CARD_ENABLED apagado y sin planes en Stripe, el único
            método visible es la billetera: prometer "cualquiera de los dos
            métodos" describía una pantalla que el chofer no ve. */}
        <View style={styles.helpBox}>
          <Ionicons name="information-circle" size={18} color={Colors.primary} />
          <Text style={styles.helpText}>
            {CUOTA_TARJETA_ENABLED
              ? 'Tu cuota se paga con tarjeta (pago único, desde Billetera) o recargando la billetera por transferencia o depósito con tu comprobante. No hay cobros automáticos a tu tarjeta. Tu crédito acumulado siempre se aplica primero, así que solo pagas lo que falte.'
              : 'Tu cuota se paga recargando la billetera: depositas o transfieres el monto de tu cuota y subes el comprobante. Tu crédito acumulado siempre se aplica primero, así que solo pagas lo que falte.'}
          </Text>
        </View>

        {/* DESTINO DE INCENTIVOS — bonos por meta, hitos y referidos */}
        <Text style={styles.sectionTitle}>¿Dónde recibes tus incentivos?</Text>
        <View style={styles.methodCard}>
          <View style={[styles.methodIcon, { backgroundColor: '#FF9500' }]}>
            <Ionicons name="gift" size={22} color="#FFF" />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.methodTitle}>Bonos, metas y referidos</Text>
            <Text style={styles.methodSub}>
              {incentiveMethod === 'WALLET'
                ? 'Se abonan a tu saldo al instante y descuentan tu cuota'
                : 'Te los transferimos a tu cuenta bancaria registrada'}
            </Text>
          </View>
          {savingPref ? <ActivityIndicator color={Colors.primary} /> : null}
        </View>
        <View style={styles.choiceRow}>
          <TouchableOpacity
            style={[styles.choiceBtn, incentiveMethod === 'WALLET' && styles.choiceBtnActive]}
            onPress={() => setIncentiveMethod('WALLET')}
            disabled={savingPref}
          >
            <Ionicons name="wallet" size={16} color={incentiveMethod === 'WALLET' ? '#FFF' : Colors.primary} />
            <Text style={[styles.choiceBtnText, incentiveMethod === 'WALLET' && { color: '#FFF' }]}>Mi saldo</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.choiceBtn, incentiveMethod === 'TRANSFER' && styles.choiceBtnActive]}
            onPress={() => setIncentiveMethod('TRANSFER')}
            disabled={savingPref}
          >
            <Ionicons name="cash" size={16} color={incentiveMethod === 'TRANSFER' ? '#FFF' : Colors.primary} />
            <Text style={[styles.choiceBtnText, incentiveMethod === 'TRANSFER' && { color: '#FFF' }]}>Transferencia</Text>
          </TouchableOpacity>
        </View>

        {incentiveMethod === 'TRANSFER' && (
          <TouchableOpacity
            onPress={() => router.push('/driver-bank-accounts')}
            style={{
              flexDirection: 'row', alignItems: 'center', gap: 10,
              backgroundColor: '#FFF8E6', borderWidth: 1, borderColor: '#F0D48A',
              borderRadius: 12, padding: 14, marginTop: 12,
            }}
          >
            <Ionicons name="information-circle" size={20} color="#7A5A12" />
            <Text style={{ flex: 1, fontSize: 12.5, color: '#7A5A12', lineHeight: 18 }}>
              Para transferirte necesitamos tu cuenta bancaria registrada.
              <Text style={{ fontWeight: '800' }}> Toca aquí para agregarla o revisarla.</Text>
            </Text>
          </TouchableOpacity>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F5F7FA' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#E4E7EB',
    backgroundColor: '#FFF',
  },
  backButton: { padding: 8, marginLeft: -8 },
  headerTitle: { fontSize: 18, fontWeight: 'bold', color: Colors.text },
  container: { padding: 20 },

  choiceRow: { flexDirection: 'row', gap: 10, marginBottom: 8 },
  choiceBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Colors.primary,
    backgroundColor: '#FFF',
  },
  choiceBtnActive: { backgroundColor: Colors.primary },
  choiceBtnText: { fontSize: 13, fontWeight: '700', color: Colors.primary },

  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 16,
    marginBottom: 18,
    borderWidth: 1,
  },
  bannerSuccess: { backgroundColor: '#E8F5E9', borderColor: '#A5D6A7' },
  bannerWarn: { backgroundColor: '#FFF3E0', borderColor: '#FFCC80' },
  bannerError: { backgroundColor: '#FFEBEE', borderColor: '#FFCDD2' },
  bannerNeutral: { backgroundColor: '#E3F2FD', borderColor: '#90CAF9' },
  bannerTitle: { fontSize: 15, fontWeight: 'bold', marginBottom: 4 },
  bannerSub: { fontSize: 12, color: '#333', lineHeight: 17 },

  // Aviso de vehículo sin registrar. Ámbar (hay que hacer algo) y no rojo:
  // no está bloqueado, pero está trabajando sin identificarse.
  avisoVehiculo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 16,
    borderRadius: 16,
    marginBottom: 18,
    borderWidth: 1,
    backgroundColor: '#FFF6E5',
    borderColor: '#F0C879',
  },
  avisoVehiculoTitulo: { fontSize: 15, fontWeight: 'bold', color: '#B26A00', marginBottom: 4 },
  avisoVehiculoTexto: { fontSize: 12, color: '#7A5A12', lineHeight: 17 },
  avisoVehiculoAccion: {
    fontSize: 13,
    fontWeight: '800',
    color: '#B26A00',
    marginTop: 8,
  },

  subsidyCard: {
    backgroundColor: '#FFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: Colors.primary,
  },
  subsidyTitle: { fontSize: 13, fontWeight: '600', color: Colors.primary, marginLeft: 8 },
  subsidyAmount: { fontSize: 28, fontWeight: '900', color: Colors.primary, marginBottom: 6 },
  subsidySub: { fontSize: 12, color: '#666', lineHeight: 17 },
  progressBg: {
    height: 8,
    backgroundColor: '#ECEFF1',
    borderRadius: 4,
    marginTop: 12,
    overflow: 'hidden',
  },
  progressFill: { height: '100%', borderRadius: 4 },
  progressLabel: { fontSize: 11, color: '#666', marginTop: 6, fontStyle: 'italic' },
  howCreditWorks: {
    flexDirection: 'row',
    backgroundColor: '#F5F7FA',
    padding: 12,
    borderRadius: 10,
    marginBottom: 18,
    alignItems: 'flex-start',
  },
  howCreditText: { flex: 1, marginLeft: 8, fontSize: 11, color: '#666', lineHeight: 16 },

  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },
  methodCard: {
    backgroundColor: '#FFF',
    borderRadius: 14,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#E4E7EB',
  },
  methodCardActive: { borderColor: Colors.primary, backgroundColor: 'rgba(0, 45, 98, 0.03)' },
  methodIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    backgroundColor: Colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  methodTitle: { fontSize: 15, fontWeight: 'bold', color: Colors.text },
  methodSub: { fontSize: 12, color: '#666', marginTop: 2 },
  methodAction: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: 'rgba(0, 45, 98, 0.08)',
  },
  methodActionText: { color: Colors.primary, fontWeight: 'bold', fontSize: 13 },

  helpBox: {
    flexDirection: 'row',
    backgroundColor: '#F0F4FF',
    padding: 14,
    borderRadius: 12,
    marginTop: 18,
    alignItems: 'flex-start',
  },
  helpText: { flex: 1, marginLeft: 10, fontSize: 12, color: Colors.primary, lineHeight: 17 },
});
